"use client";

import { useUser } from "@/components/clients/LocaleToStorage";
import RouterLink from "@/components/ui/router-link";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";

import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/messages/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

import useSWR from "swr";

function DownloadWithFileName(props: {
  file: string;
  icon: string;
  title: string;
}) {
  const locale = useCurrentLocale();

  const pathname = usePathname();

  const { user } = useUser();

  const formatDate = locale === "en" ? "yyyy-MM-dd" : "yyyy年 MM月 dd日";

  const { data } = useSWR<{
    data: {
      id: string;
      filename_download: string;
      title: string;
      type: string;
      uploaded_on: string;
    };
  }>(
    () =>
      props.file
        ? `${process.env.NEXT_PUBLIC_API_HOST}/files/${props.file}`
        : null,
    async (url: string) => {
      return fetch(url).then((res) => res.json());
    }
  );

  if (!data) {
    return null;
  }

  if (!user) {
    return (
      <RouterLink
        href={`/login?redirect_url=${pathname}`}
        className="flex h-[5.5rem] w-full items-center rounded-lg bg-white px-3 py-5 shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7">
        <img
          className="h-10 w-10 md:h-[60px] md:w-[60px]"
          src={props.icon}
          alt={props.title}
        />

        <div className="w-full flex-1 pl-2 md:pl-0">
          <div
            className={cn("text-base font-medium md:pt-2 md:text-xl", {
              "md:text-lg": locale === "en",
            })}>
            {props.title}
          </div>

          <div className="text-xs text-[#808080] md:hidden">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>
        </div>

        <div className="flex items-center justify-between md:w-full">
          <div className="hidden text-sm text-[#808080] md:block">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>

          <div className="flex items-center space-x-2 text-primary">
            <span>PDF</span>
            <Download />
          </div>
        </div>
      </RouterLink>
    );
  }

  return (
    <a
      target="_blank"
      href={`${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${data.data?.id}/${data.data?.filename_download}`}>
      <div className="flex h-[5.5rem] w-full items-center rounded-lg bg-white px-3 py-5 shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7">
        <img
          className="h-10 w-10 md:h-[60px] md:w-[60px]"
          src={props.icon}
          alt={props.title}
        />

        <div className="w-full flex-1 pl-2 md:pl-0">
          <div
            className={cn("text-base font-medium md:pt-2 md:text-xl", {
              "md:text-lg": locale === "en",
            })}>
            {props.title}
          </div>

          <div className="text-xs text-[#808080] md:hidden">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>
        </div>

        <div className="flex items-center justify-between md:w-full">
          <div className="hidden text-sm text-[#808080] md:block">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>

          <div className="flex items-center space-x-2 text-primary">
            <span>PDF</span>
            <Download />
          </div>
        </div>
      </div>
    </a>
  );
}

function DownloadDataSheet(props: {
  dataSheet: (string | null | undefined)[];
  datasheetTexts?: (string | null | undefined)[];
}) {
  const t = useScopedI18n("产品");

  const locale = useCurrentLocale();

  const pathname = usePathname();

  const { user } = useUser();

  const formatDate = locale === "en" ? "yyyy-MM-dd" : "yyyy年 MM月 dd日";

  const currentFile = useMemo(() => {
    const level = user?.role?.level;
    if (level) {
      return props.dataSheet[level - 1];
    }

    return props.dataSheet.filter(Boolean)?.[0];
  }, [user, props.dataSheet]);

  const currentFileName = useMemo(() => {
    const level = user?.role?.level;
    if (level) {
      return props.datasheetTexts?.[level - 1];
    }
  }, [user, props.datasheetTexts]);

  const { data } = useSWR<{
    data: {
      id: string;
      filename_download: string;
      title: string;
      type: string;
      uploaded_on: string;
    };
  }>(
    () =>
      currentFile
        ? `${process.env.NEXT_PUBLIC_API_HOST}/files/${currentFile}`
        : null,
    async (url: string) => {
      return fetch(url).then((res) => res.json());
    }
  );

  if (!data?.data) {
    return null;
  }

  if (!user) {
    return (
      <RouterLink
        href={`/login?redirect_url=${pathname}`}
        className="flex h-[5.5rem] w-full items-center rounded-lg bg-white px-3 py-5 shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7">
        <img
          className="h-10 w-10 md:h-[60px] md:w-[60px]"
          src="/icons/datasheet.svg"
          alt="DataSheet"
        />

        <div className="w-full flex-1 pl-2 md:pl-0">
          <div
            className={cn("text-base font-medium md:pt-2 md:text-xl", {
              "md:text-lg": locale === "en",
            })}>
            {t("数据表")}
          </div>

          <div className="text-xs text-[#808080] md:hidden">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>
        </div>

        <div className="flex items-center justify-between md:w-full">
          <div className="hidden text-sm text-[#808080] md:block">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>

          <div className="flex items-center space-x-2 text-primary">
            <span>PDF</span>
            <Download />
          </div>
        </div>
      </RouterLink>
    );
  }

  return (
    <a
      target="_blank"
      href={`${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${data.data?.id}/${data.data?.filename_download}`}
      className="flex h-[5.5rem] w-full items-center rounded-lg bg-white px-3 py-5 shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7">
      <img
        className="h-10 w-10 md:h-[60px] md:w-[60px]"
        src="/icons/datasheet.svg"
        alt="DataSheet"
      />

      <div className="w-full flex-1 pl-2 md:pl-0">
        <div
          className={cn("text-base font-medium md:pt-2 md:text-xl", {
            "md:text-lg": locale === "en",
          })}>
          {currentFileName ?? t("数据表")}
        </div>

        <div className="text-xs text-[#808080] md:hidden">
          {data.data?.uploaded_on
            ? data.data.uploaded_on.substring(0, 10)
            : null}
        </div>
      </div>

      <div className="flex items-center justify-between md:w-full">
        <div className="hidden text-sm text-[#808080] md:block">
          {data.data?.uploaded_on
            ? data.data.uploaded_on.substring(0, 10)
            : null}
        </div>

        <div className="flex items-center space-x-2 text-primary">
          <span>PDF</span>
          <Download />
        </div>
      </div>
    </a>
  );
}

export default function Document(props: {
  application_file?: string | null;
  datasheet?: (string | null | undefined)[];
  datasheetTexts?: (string | null | undefined)[];
  evm_file?: string | null;
}) {
  const t = useScopedI18n("产品");

  if (
    !props.datasheet?.filter(Boolean).length &&
    !props.evm_file &&
    !props.application_file
  ) {
    return <div className="pt-10 md:pt-28"></div>;
  }

  return (
    <div className="relative pt-10 md:pt-28">
      <div
        id="technical-documentation"
        className="pointer-events-none absolute -top-[46px]"></div>
      <h2 className="text-center text-xl md:text-left md:text-4xl">
        {t("技术文档")}
      </h2>

      <div className="mt-5 grid grid-cols-1 gap-4 md:mt-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
        {props.datasheet && props.datasheet.filter(Boolean).length > 0 && (
          <DownloadDataSheet
            dataSheet={props.datasheet}
            datasheetTexts={props.datasheetTexts}
          />
        )}

        {props.evm_file && (
          <DownloadWithFileName
            icon="/icons/evm.svg"
            title={t("EVM用户指南")}
            file={props.evm_file}
          />
        )}

        {props.application_file && (
          <DownloadWithFileName
            icon="/icons/application.svg"
            title={t("产品应用手册")}
            file={props.application_file}
          />
        )}
      </div>
    </div>
  );
}
